import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Urls from "../../shared/api/Urls";

interface ShutdownStatus {
  active: boolean;
  message?: string;
  endsAt?: number;
}

export default function ShutdownDialog() {
  const [status, setStatus] = useState<ShutdownStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      const resp = await fetch(Urls.SHUTDOWN_STATUS);
      if (!resp.ok) return;
      const data: ShutdownStatus = await resp.json();
      if (!data.active) {
        // Shutdown cancelled — clear everything
        if (status?.active) {
          setDismissed(false);
          setFeedbackSent(false);
        }
        setStatus(data);
        return;
      }
      setStatus(data);
    } catch {
      // ignore network errors
    }
  }, [status?.active]);

  // Poll every 30s
  useEffect(() => {
    poll();
    const id = setInterval(poll, 30_000);
    return () => clearInterval(id);
  }, [poll]);

  // Listen for socket events if lobby socket exists on window
  useEffect(() => {
    // The lobby socket is only available when in a lobby, so we try to find it
    // We'll handle this via the polling mechanism primarily
    // Socket events provide instant notification as a bonus when connected
  }, []);

  // Countdown timer
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!status?.active || !status.endsAt) {
      setCountdown("");
      return;
    }
    function tick() {
      const remaining = Math.max(0, (status!.endsAt ?? 0) - Date.now());
      if (remaining <= 0) {
        setCountdown(t("shutdown_now"));
        return;
      }
      const mins = Math.floor(remaining / 60_000);
      const secs = Math.floor((remaining % 60_000) / 1000);
      setCountdown(`${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`);
    }
    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [status?.active, status?.endsAt]);

  async function sendFeedback() {
    if (!feedbackText.trim()) return;
    try {
      await fetch(Urls.SHUTDOWN_FEEDBACK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from: "User", text: feedbackText.trim() }),
      });
      setFeedbackSent(true);
      setFeedbackText("");
    } catch {
      // ignore
    }
  }

  const show = status?.active && !dismissed;
  const showReopenButton = status?.active && dismissed;

  return (
    <>
    {showReopenButton && (
      <Box
        onClick={() => setDismissed(false)}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 1299,
          bgcolor: "warning.main",
          color: "warning.contrastText",
          borderRadius: 2,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          boxShadow: 3,
          "&:hover": { bgcolor: "warning.dark" },
        }}
      >
        <WarningAmberIcon fontSize="small" />
        <Typography variant="body2" fontWeight="bold">{countdown}</Typography>
      </Box>
    )}
    <Dialog open={!!show} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon color="warning" />
        {t("shutdown_title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="h3" align="center" sx={{ fontFamily: "monospace", my: 2 }}>
          {countdown}
        </Typography>
        {status?.message && (
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
            {status.message}
          </Typography>
        )}
        {!feedbackSent ? (
          <Box display="flex" gap={1} mt={2}>
            <TextField
              size="small"
              fullWidth
              placeholder={t("shutdown_message_placeholder")}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendFeedback()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={sendFeedback}
              disabled={!feedbackText.trim()}
              sx={{ flexShrink: 0 }}
            >
              {t("send")}
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="success.main" align="center" sx={{ mt: 2 }}>
            {t("shutdown_message_sent")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDismissed(true)}>{t("dismiss")}</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
